import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, ChevronRight, Terminal, Loader2, Code2, Ban, Copy, Check, BookOpen, FileText, FileDown, ThumbsUp, ThumbsDown, RefreshCw } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { QAgentMessage, QAgentCitation, QAgentDocumentNotice } from '../../services/QAgent';
import { MarkdownRenderer } from '../ui/markdown-renderer';
import { stripToolResults } from './strip-tool-results';
import { AgentOrb } from '../ui/agent-orb';
import { getToolStatusLabel, getToolStatusOrb, workingStatusLabel } from './tool-status';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Button } from '../ui/button';
import queriIcon from '../../assets/queri-icon.png';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { useBoundStore } from '../../store';
import { useI18n } from '../../i18n';
import type { Dictionary } from '../../i18n';
import { cn } from '@/lib/utils';

// Format timestamp in user's local timezone with friendly relative display
const formatMessageTime = (t: Dictionary, isoString?: string): string | null => {
  if (!isoString) return null;
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return null;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const msgDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

  if (msgDay.getTime() === today.getTime()) {
    return time;
  }
  if (msgDay.getTime() === yesterday.getTime()) {
    return t.qagentDocument.chatMessage.yesterday(time);
  }
  const dateStr = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return t.qagentDocument.chatMessage.dateTime(dateStr, time);
};

// Renders text with @[mentions] styled as inline chips
const renderTextWithMentions = (text: string) => {
  // Match @[Name] bracketed mentions - file extensions detected inside brackets
  const fileExtRegex = /\.(pdf|docx?|txt|md|xlsx?)$/i;
  const mentionRegex = /@\[([^\]]+)\]/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    // Text before the mention
    if (match.index > lastIndex) {
      parts.push(<span key={`t-${lastIndex}`}>{text.slice(lastIndex, match.index)}</span>);
    }
    const mentionName = match[1];
    const isFile = fileExtRegex.test(mentionName);
    parts.push(
      <span
        key={`m-${match.index}`}
        className="inline-flex items-center gap-1 bg-brand-light text-brand rounded-md px-1.5 py-0.5 text-[0.9em] font-medium align-baseline border border-brand/20 mx-0.5"
      >
        {isFile ? <FileText className="h-3 w-3" /> : <BookOpen className="h-3 w-3" />}
        {mentionName}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }

  // Remaining text
  if (lastIndex === 0) return text; // no mentions found, return plain string
  if (lastIndex < text.length) {
    parts.push(<span key={`t-${lastIndex}`}>{text.slice(lastIndex)}</span>);
  }
  return <>{parts}</>;
};

export { stripToolResults };

// Maps a skill id to a translated label
const getSkillLabel = (t: Dictionary, skillId: string): string => {
  const map: Record<string, string> = {
    review: t.qagentDocument.chatMessage.skillLabels.review,
  };
  return map[skillId] || t.qagentDocument.chatMessage.defaultWorking;
};

const ThinkingIndicator = ({ toolStatus, skillId, progress }: { toolStatus?: string | null; skillId?: string | null; progress?: number | null }) => {
  const { t } = useI18n();
  const defaultLabel = skillId ? getSkillLabel(t, skillId) : t.qagentDocument.chatMessage.queriing;
  const displayText = toolStatus ? getToolStatusLabel(t, toolStatus) : defaultLabel;
  return (
    <div className="flex flex-col gap-1.5">
      <div role="status" aria-live="polite" className="flex items-center gap-2">
        <AgentOrb state={getToolStatusOrb(toolStatus)} />
        <AnimatePresence mode="wait">
          <motion.span
            key={displayText}
            className="text-sm text-muted-foreground/60"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
          >
            {displayText}
          </motion.span>
        </AnimatePresence>
      </div>
      {progress != null && progress > 0 && (
        <div className="w-48 h-1 bg-muted rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-brand/60 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
      )}
    </div>
  );
};

interface ToolInvocation {
  state: 'partial-call' | 'call' | 'result';
  toolName: string;
  args?: Record<string, any>;
  result?: {
    __cancelled?: boolean;
    [key: string]: any;
  };
}

interface ReasoningPart {
  type: 'reasoning';
  reasoning: string;
  durationMs?: number;
}

interface TextPart {
  type: 'text';
  text: string;
}

interface ToolInvocationPart {
  type: 'tool-invocation';
  toolInvocation: ToolInvocation;
}

type MessagePart = TextPart | ReasoningPart | ToolInvocationPart;

interface QAgentChatMessageProps {
  message: QAgentMessage;
  isStreaming?: boolean;
  toolStatus?: string | null;
  isLastAiMessage?: boolean;
  onRegenerate?: () => void;
  /** Download this answer as a Word document (receives cleaned markdown) */
  onExport?: (markdown: string) => Promise<void>;
  skillId?: string | null;
  progress?: number | null;
}

export const QAgentChatMessage: React.FC<QAgentChatMessageProps> = ({
  message,
  isStreaming,
  toolStatus,
  isLastAiMessage,
  onRegenerate,
  onExport,
  skillId,
  progress,
}) => {
  const { t } = useI18n();
  const isUser = message.type === 'human';
  const user = useBoundStore((state) => state.user);
  const [copied, setCopied] = useState(false);
  const [reaction, setReaction] = useState<'like' | 'dislike' | null>(null);

  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(stripToolResults(message.content));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const [exporting, setExporting] = useState(false);
  const handleExport = onExport
    ? async () => {
        setExporting(true);
        try {
          await onExport(stripToolResults(message.content));
        } finally {
          setExporting(false);
        }
      }
    : undefined;

  // Parse parts if available (for reasoning, tool calls, etc.)
  const parts: MessagePart[] = (message as any).parts || [];
  const toolInvocations: ToolInvocation[] = (message as any).toolInvocations || message.toolCalls || [];

  const isQueued = message.id.startsWith('queued-');

  if (isUser) {
    return (
      <div className={cn("py-3 md:py-4", isQueued && "opacity-50")}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex justify-end gap-3 items-start">
            <div className="max-w-[75%]">
              <div className="rounded-2xl rounded-br-sm px-4 py-2.5" style={{ backgroundColor: '#e6f0eb' }}>
                <p className="text-[15px] md:text-base text-foreground leading-relaxed whitespace-pre-wrap">
                  {renderTextWithMentions(message.content)}
                </p>
                {isQueued && (
                  <p className="text-xs text-muted-foreground mt-1">{t.qagentDocument.chatMessage.queued}</p>
                )}
              </div>
              {formatMessageTime(t, message.createdAt) && (
                <p className="text-[11px] text-muted-foreground/50 mt-1 text-right">
                  {formatMessageTime(t, message.createdAt)}
                </p>
              )}
            </div>
            <Avatar className="h-8 w-8 shrink-0 mt-1.5">
              <AvatarFallback className="bg-muted text-brand text-xs font-semibold">
                {getUserInitials()}
              </AvatarFallback>
            </Avatar>
          </div>
        </div>
      </div>
    );
  }

  // Skip tool-type messages entirely (these are tool results, not user-visible)
  if (message.type === 'tool') return null;

  // Skip AI messages that only have tool invocations and no visible text
  const strippedContent = stripToolResults(message.content);
  const hasVisibleText = strippedContent.length > 0;
  const hasOnlyToolCalls = toolInvocations.length > 0 && !hasVisibleText && parts.length === 0;
  if (hasOnlyToolCalls && !isStreaming) return null;

  // Skip if completely empty after stripping
  // Also skip if parts only contain tool invocations (no text or reasoning)
  const hasVisibleParts = parts.some(p => p.type === 'text' || p.type === 'reasoning');
  const hasVisibleContent = hasVisibleParts || (parts.length === 0 && hasVisibleText);
  if (!hasVisibleContent && !isStreaming) return null;

  // AI message with support for parts (reasoning, tool calls)
  return (
    <div className="py-3 md:py-4">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex gap-3 items-start">
          {/* QAgent Avatar */}
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-brand-light border border-brand/30 -mt-[2px]">
            <img src={queriIcon} alt="QAgent" className="h-5 w-5" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-3">
            {/* Render parts if available */}
            {parts.length > 0 ? (
              parts.map((part, index) => {
                if (part.type === 'reasoning') {
                  return <ReasoningBlock key={`reasoning-${index}`} part={part} />;
                }
                // Skip tool invocation parts - suggestions are shown as SuggestionCards
                if (part.type === 'tool-invocation') {
                  return null;
                }
                if (part.type === 'text') {
                  return (
                    <MessageContent
                      key={`text-${index}`}
                      content={part.text}
                      isStreaming={isStreaming && index === parts.length - 1}
                      toolStatus={toolStatus}
                      skillId={skillId}
                      onCopy={handleCopy}
                      copied={copied}
                      isLastAiMessage={isLastAiMessage}
                      onRegenerate={isLastAiMessage ? onRegenerate : undefined}
                      onExport={handleExport}
                      exporting={exporting}
                      reaction={reaction}
                      onReaction={setReaction}
                      progress={progress}
                    />
                  );
                }
                return null;
              })
            ) : (
              <>
                {/* Main content (tool invocations hidden - shown as SuggestionCards) */}
                <MessageContent
                  content={message.content}
                  isStreaming={isStreaming}
                  toolStatus={toolStatus}
                  skillId={skillId}
                  onCopy={handleCopy}
                  copied={copied}
                  isLastAiMessage={isLastAiMessage}
                  onRegenerate={isLastAiMessage ? onRegenerate : undefined}
                  onExport={handleExport}
                  exporting={exporting}
                  reaction={reaction}
                  onReaction={setReaction}
                  progress={progress}
                />
                {message.notices && message.notices.length > 0 && (
                  <DocumentNotices notices={message.notices} />
                )}
                {message.citations && message.citations.length > 0 && (
                  <CitationSources citations={message.citations} />
                )}
              </>
            )}
            {!isStreaming && formatMessageTime(t, message.createdAt) && (
              <p className="text-[11px] text-muted-foreground/50 mt-1">
                {formatMessageTime(t, message.createdAt)}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Reasoning/Thinking block component
const ReasoningBlock: React.FC<{ part: ReasoningPart }> = ({ part }) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);

  const formatDuration = (ms?: number) => {
    if (!ms) return '';
    const seconds = Math.round(ms / 1000);
    return `${seconds}s`;
  };

  return (
    <div className="mb-2">
      <Collapsible
        open={isOpen}
        onOpenChange={setIsOpen}
        className="group w-full overflow-hidden rounded-lg border bg-muted/50"
      >
        <div className="flex items-center p-2">
          <CollapsibleTrigger asChild>
            <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <ChevronRight className="h-4 w-4 transition-transform group-data-[state=open]:rotate-90" />
              <span className="font-medium">{t.qagentDocument.chatMessage.thinking}</span>
              {part.durationMs && (
                <span className="text-xs opacity-60">
                  {formatDuration(part.durationMs)}
                </span>
              )}
            </button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent forceMount>
          <motion.div
            initial={false}
            animate={isOpen ? 'open' : 'closed'}
            variants={{
              open: { height: 'auto', opacity: 1 },
              closed: { height: 0, opacity: 0 },
            }}
            transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
            className="border-t"
          >
            <div className="p-3">
              <div className="whitespace-pre-wrap text-xs text-muted-foreground leading-relaxed">
                {part.reasoning}
              </div>
            </div>
          </motion.div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

// Tool call block component
const ToolCallBlock: React.FC<{ invocation: ToolInvocation }> = ({ invocation }) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const isCancelled = invocation.state === 'result' && invocation.result?.__cancelled === true;

  if (isCancelled) {
    return (
      <div className="flex items-center gap-2 rounded-lg border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
        <Ban className="h-4 w-4" />
        <span>
          {t.qagentDocument.chatMessage.cancelled} <code className="font-mono text-xs">{invocation.toolName}</code>
        </span>
      </div>
    );
  }

  if (invocation.state === 'partial-call' || invocation.state === 'call') {
    return (
      <div className="flex items-center gap-2 rounded-lg border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
        <Terminal className="h-4 w-4 text-brand" />
        <span>
          {t.qagentDocument.chatMessage.running} <code className="font-mono text-xs">{invocation.toolName}</code>
        </span>
        <Loader2 className="h-3 w-3 animate-spin ml-auto" />
      </div>
    );
  }

  // Result state
  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="group overflow-hidden rounded-lg border bg-muted/50"
    >
      <div className="flex items-center px-3 py-2">
        <CollapsibleTrigger asChild>
          <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ChevronRight className="h-4 w-4 transition-transform group-data-[state=open]:rotate-90" />
            <Code2 className="h-4 w-4 text-brand" />
            <span>
              <code className="font-mono text-xs">{invocation.toolName}</code>
            </span>
          </button>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent forceMount>
        <motion.div
          initial={false}
          animate={isOpen ? 'open' : 'closed'}
          variants={{
            open: { height: 'auto', opacity: 1 },
            closed: { height: 0, opacity: 0 },
          }}
          transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
          className="border-t"
        >
          <div className="p-3">
            <pre className="overflow-x-auto whitespace-pre-wrap text-xs text-foreground">
              {JSON.stringify(invocation.result, null, 2)}
            </pre>
          </div>
        </motion.div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Message content with markdown
const MessageContent: React.FC<{
  content: string;
  isStreaming?: boolean;
  toolStatus?: string | null;
  skillId?: string | null;
  onCopy: () => void;
  copied: boolean;
  isLastAiMessage?: boolean;
  onRegenerate?: () => void;
  onExport?: () => void;
  exporting?: boolean;
  reaction: 'like' | 'dislike' | null;
  onReaction: (r: 'like' | 'dislike' | null) => void;
  progress?: number | null;
}> = ({ content, isStreaming, toolStatus, skillId, onCopy, copied, isLastAiMessage, onRegenerate, onExport, exporting, reaction, onReaction, progress }) => {
  const { t } = useI18n();
  // Show thinking indicator with contextual status when streaming starts but no content yet
  if (isStreaming && !content) {
    return <ThinkingIndicator toolStatus={toolStatus} skillId={skillId} progress={progress} />;
  }

  const cleanContent = stripToolResults(content);
  if (!cleanContent) return null;

  return (
    <div className="group/message relative">
      <div className="prose prose-sm md:prose-base dark:prose-invert max-w-none text-foreground [&>div>*:first-child]:mt-0">
        <MarkdownRenderer>{cleanContent}</MarkdownRenderer>
      </div>
      {/* The orb stays until the reply ends, also while the model thinks between tool calls */}
      {isStreaming && (
        <div role="status" aria-live="polite" className="flex items-center gap-2 mt-2 text-xs text-muted-foreground/60">
          <AgentOrb state={getToolStatusOrb(toolStatus)} />
          <span>{workingStatusLabel(t, toolStatus, skillId)}</span>
        </div>
      )}

      {/* Action buttons - always visible on last AI message, hover on others */}
      {!isStreaming && cleanContent && (
        <div
          className={cn(
            "flex items-center gap-0.5 mt-1.5 transition-opacity",
            isLastAiMessage ? "opacity-100" : "opacity-0 group-hover/message:opacity-100 duration-300"
          )}
        >
          {/* Copy */}
          <button
            onClick={onCopy}
            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title={t.qagentDocument.chatMessage.copy}
          >
            <AnimatePresence mode="wait" initial={false}>
              {copied ? (
                <motion.span key="check" initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.5 }} transition={{ duration: 0.15 }}>
                  <Check className="h-3.5 w-3.5 text-emerald-500" />
                </motion.span>
              ) : (
                <motion.span key="copy" initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.5 }} transition={{ duration: 0.15 }}>
                  <Copy className="h-3.5 w-3.5" />
                </motion.span>
              )}
            </AnimatePresence>
          </button>

          {/* Download as Word */}
          {onExport && (
            <button
              onClick={onExport}
              disabled={exporting}
              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors disabled:opacity-60"
              title={t.qagentDocument.chatMessage.exportWord}
              aria-label={t.qagentDocument.chatMessage.exportWord}
            >
              {exporting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileDown className="h-3.5 w-3.5" />
              )}
            </button>
          )}

          {/* Like */}
          <button
            onClick={() => onReaction(reaction === 'like' ? null : 'like')}
            className={cn(
              "p-1 rounded border transition-colors",
              reaction === 'like'
                ? "border-brand text-brand"
                : "border-transparent text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
            title={t.qagentDocument.chatMessage.like}
          >
            <ThumbsUp className="h-3.5 w-3.5" fill={reaction === 'like' ? 'currentColor' : 'none'} />
          </button>

          {/* Dislike */}
          <button
            onClick={() => onReaction(reaction === 'dislike' ? null : 'dislike')}
            className={cn(
              "p-1 rounded border transition-colors",
              reaction === 'dislike'
                ? "border-brand text-brand"
                : "border-transparent text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
            title={t.qagentDocument.chatMessage.dislike}
          >
            <ThumbsDown className="h-3.5 w-3.5" fill={reaction === 'dislike' ? 'currentColor' : 'none'} />
          </button>

          {/* Regenerate - only on last AI message */}
          {onRegenerate && (
            <button
              onClick={onRegenerate}
              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              title={t.qagentDocument.chatMessage.regenerate}
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Short notice when the model saw a document only in part
// One notice per document. A long document the assistant can read section by
// section ("summarised") is not cut off, so that notice wins over "truncated".
export const uniqueDocumentNotices = (notices: QAgentDocumentNotice[]): QAgentDocumentNotice[] => {
  const byFile = new Map<string, QAgentDocumentNotice>();
  for (const n of notices) {
    const seen = byFile.get(n.fileName);
    if (!seen || (seen.code === 'file_truncated' && n.code === 'file_summarised')) {
      byFile.set(n.fileName, n);
    }
  }
  return Array.from(byFile.values());
};

const DocumentNotices: React.FC<{ notices: QAgentDocumentNotice[] }> = ({ notices }) => {
  const { t } = useI18n();
  return (
    <div className="mt-2 space-y-1" role="note">
      {uniqueDocumentNotices(notices).map((n, i) => (
        <p
          key={i}
          className="flex items-start gap-1.5 text-xs rounded border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800/60 text-amber-900 dark:text-amber-200 px-2 py-1"
        >
          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
          <span>
            {n.code === 'file_truncated'
              ? t.qagentDocument.documentNotices.truncated(n.fileName, n.shownChars, n.totalChars)
              : t.qagentDocument.documentNotices.summarised(n.fileName)}
          </span>
        </p>
      ))}
    </div>
  );
};

// Collapsible citation sources section
const CitationSources: React.FC<{ citations: QAgentCitation[] }> = ({ citations }) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);

  if (citations.length === 0) return null;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mt-2">
      <CollapsibleTrigger className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
        <ChevronRight
          className={cn("h-3 w-3 transition-transform", isOpen && "rotate-90")}
        />
        <FileText className="h-3 w-3" />
        <span>{t.qagentDocument.citations.sources(citations.length)}</span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-1.5 space-y-1.5 pl-5">
          {citations.map((c, i) => (
            <div key={i} className="text-xs border-l-2 border-brand/20 pl-2 py-0.5">
              {/* The [N] the answer uses for this source */}
              <span className="mr-1 text-muted-foreground">[{c.index ?? i + 1}]</span>
              {c.uri ? (
                <a
                  href={c.uri}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-brand hover:underline"
                >
                  {c.fileName}
                </a>
              ) : (
                <span className="font-medium text-foreground">{c.fileName}</span>
              )}
              {c.chunkText && (
                <p className="text-muted-foreground mt-0.5 line-clamp-2">
                  {c.chunkText.substring(0, 200)}{c.chunkText.length > 200 ? '...' : ''}
                </p>
              )}
              {c.retrievedAt && (
                <p className="text-muted-foreground/60 mt-0.5 text-[10px]">
                  {t.qagentDocument.citations.retrieved(new Date(c.retrievedAt).toLocaleDateString())}
                </p>
              )}
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default QAgentChatMessage;
